<template>
    <div class="user-onboarding">
        <!-- Progress Stepper -->
        <q-stepper
            v-model="currentStep"
            vertical
            color="primary"
            animated
            header-nav
            ref="stepper"
        >
            <q-step
                v-for="(step, index) in localSteps"
                :key="step.id"
                :name="index"
                :title="step.title"
                :caption="getStepCaption(step)"
                :icon="getStepIcon(step)"
                :active-icon="getStepIcon(step)"
                :done="step.status === 'completed'"
                :error="
                    step.status === 'pending' && step.type === 'confirmation'
                "
                :disable="index > firstIncompleteIndex"
            >
                <div class="q-pa-md bg-grey-1 rounded-borders q-mb-md">
                    <div v-if="step.type === 'document'">
                        <RichTextEditor
                            :model-value="step.content?.html || ''"
                            :read-only="true"
                        />
                    </div>

                    <!-- Type: Video -->
                    <div v-else-if="step.type === 'video'">
                        <div v-if="getStepVideo(step)" class="q-mb-md">
                            <div
                                v-if="isStepVideoProcessing(step)"
                                class="q-pa-md bg-white rounded-borders shadow-1 text-center"
                            >
                                <q-spinner-dots
                                    size="lg"
                                    color="primary"
                                    class="q-mb-sm"
                                />
                                <div
                                    class="text-weight-bold text-primary text-subtitle1"
                                >
                                    {{ $t("onboarding.video_preparing") }}
                                </div>
                                <div class="text-caption text-grey q-mb-md">
                                    {{ $t("onboarding.worker_processing") }} ({{
                                        getStepVideo(step)
                                            .processing_progress || 0
                                    }}%)
                                </div>
                                <q-linear-progress
                                    :value="
                                        (getStepVideo(step)
                                            .processing_progress || 0) / 100
                                    "
                                    color="secondary"
                                    size="10px"
                                    stripe
                                    animated
                                    class="rounded-borders"
                                />
                            </div>
                            <VideoPlayer
                                v-else
                                :video="getStepVideo(step)"
                                :initial-position="
                                    step.videoProgress?.position || 0
                                "
                                :downloadable="
                                    getStepVideo(step)?.isDownloadable || false
                                "
                                @watched="() => completeStep(step)"
                            />
                        </div>
                        <div
                            v-else
                            class="text-center q-pa-xl bg-grey-3 rounded-borders"
                        >
                            <q-icon name="movie" size="xl" color="primary" />
                            <p class="q-mt-md">
                                {{ $t("onboarding.no_video_attached") }}
                            </p>
                        </div>
                    </div>

                    <!-- Type: Confirmation -->
                    <div v-else-if="step.type === 'confirmation'">
                        <div class="text-center q-pa-md">
                            <q-icon
                                name="hourglass_empty"
                                size="xl"
                                color="warning"
                                class="q-mb-md"
                            />
                            <div class="text-h6 text-warning">
                                {{ $t("onboarding.waiting_confirmation") }}
                            </div>
                            <p>
                                {{ $t("onboarding.waiting_confirmation_desc") }}
                            </p>
                        </div>
                    </div>

                    <!-- Type: Email -->
                    <div v-else-if="step.type === 'email'">
                        <div class="text-center q-pa-md">
                            <q-icon
                                name="mark_email_read"
                                size="xl"
                                color="primary"
                                class="q-mb-md"
                            />
                            <div class="text-h6">
                                {{ $t("onboarding.email_trigger") }}
                            </div>
                            <p>{{ $t("onboarding.email_trigger_desc") }}</p>
                        </div>
                    </div>
                </div>

                <q-stepper-navigation>
                    <q-btn
                        v-if="
                            step.status !== 'completed' &&
                            step.type !== 'confirmation'
                        "
                        color="primary"
                        @click="completeStep(step)"
                        :label="
                            index === localSteps.length - 1
                                ? $t('onboarding.finish')
                                : $t('onboarding.mark_completed_continue')
                        "
                        :loading="loading"
                    />

                    <q-btn
                        v-if="
                            step.status === 'completed' &&
                            index < localSteps.length - 1
                        "
                        color="primary"
                        @click="stepper.next()"
                        :label="$t('onboarding.next_step')"
                        class="q-ml-sm"
                    />
                </q-stepper-navigation>
            </q-step>
        </q-stepper>
    </div>
</template>

<script setup>
import {
    ref,
    computed,
    onMounted,
    onBeforeUnmount,
    getCurrentInstance,
} from "vue";
import RichTextEditor from "./RichTextEditor.vue";
import VideoPlayer from "./VideoPlayer.vue";
import { useQuasar } from "quasar";
import axios from "axios";

const { proxy } = getCurrentInstance();
const t = (key, replacements) => proxy?.$t(key, replacements) || key;

const props = defineProps({
    steps: {
        type: Array,
        required: true,
    },
    processRoute: {
        type: String,
        required: true,
    },
    finishUrl: {
        type: String,
        required: true,
    },
});

const $q = useQuasar();
const stepper = ref(null);
const localSteps = ref([...props.steps]);
const loading = ref(false);
let pollInterval = null;

const firstIncompleteIndex = computed(() => {
    const idx = localSteps.value.findIndex((s) => s.status !== "completed");
    return idx === -1 ? localSteps.value.length - 1 : idx;
});

const currentStep = ref(firstIncompleteIndex.value);

const getStepVideo = (step) => {
    return step.video || step.content?.video || null;
};

const isStepVideoProcessing = (step) => {
    const video = getStepVideo(step);
    if (!video) return false;
    const status = video.status;
    const stage = video.processing_stage;
    return status !== "ready" && stage !== "ready" && status !== "failed";
};

onMounted(() => {
    startVideoStatusPolling();
});

onBeforeUnmount(() => {
    if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
    }
});

const checkAllVideoStatuses = async () => {
    let hasProcessing = false;

    for (let i = 0; i < localSteps.value.length; i++) {
        const step = localSteps.value[i];
        const video = getStepVideo(step);
        if (video && isStepVideoProcessing(step)) {
            hasProcessing = true;
            try {
                const res = await axios.get(`/api/videos/${video.id}/status`);
                if (res.data.video) {
                    if (step.video) step.video = res.data.video;
                    if (step.content?.video)
                        step.content.video = res.data.video;
                }
            } catch (e) {
                // Swallow polling errors
            }
        }
    }

    if (!hasProcessing && pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
    }
};

const startVideoStatusPolling = () => {
    if (pollInterval) return;
    checkAllVideoStatuses();
    pollInterval = setInterval(checkAllVideoStatuses, 2000);
};

const getStepIcon = (step) => {
    if (step.status === "completed") return "check";
    switch (step.type) {
        case "document":
            return "description";
        case "video":
            return "movie";
        case "confirmation":
            return "admin_panel_settings";
        case "email":
            return "email";
        default:
            return "radio_button_unchecked";
    }
};

const getStepCaption = (step) => {
    if (step.status === "completed") return t("onboarding.completed");
    if (step.type === "confirmation") return t("onboarding.requires_action");
    return t("onboarding.pending");
};

const completeStep = async (step) => {
    loading.value = true;
    try {
        await axios.post(props.processRoute, { step_id: step.id });

        // Update local status
        const idx = localSteps.value.findIndex((s) => s.id === step.id);
        if (idx !== -1) {
            localSteps.value[idx].status = "completed";
        }

        // Move to next or finish
        if (idx === localSteps.value.length - 1) {
            $q.notify({
                type: "positive",
                message: "Onboarding completed successfully!",
            });
            setTimeout(() => {
                window.location.href = props.finishUrl;
            }, 1000);
        } else {
            stepper.value.next();
        }
    } catch (e) {
        $q.notify({
            type: "negative",
            message: "An error occurred while saving your progress.",
        });
    } finally {
        loading.value = false;
    }
};
</script>
